import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { QueryParamsModel, QueryResultsModel } from '../../_base/crud';
import { API_ENDPOINT } from '../../_constants/endpoints';
import { CheckExistsModel } from '../_models/check-exists.model';
import { BasicUserModel, MiniUserModel, UserModel, VisitorModel } from '../_models/user.model';

const API_USERS_URL = `${API_ENDPOINT}users`;
const API_USER = `${API_ENDPOINT}user`;
const API_THIRD_URL = `${API_ENDPOINT}third-party`;
const API_UPDATE_PASSWORD = `${API_ENDPOINT}password/update`;

@Injectable({providedIn: 'root'})
export class UserService {

  constructor(private http: HttpClient) {
  }

  getUserByToken(): Observable<BasicUserModel> {
    return this.http.get<BasicUserModel>(API_USER);
  }

  getUser(payload: {ref: string, email?: string, mobile: string}): Observable<any> {
    return this.http.post<any>(`${API_USER}/get-user`, payload);
  }

  requestOtp(payload: {mobile: string}): Observable<any> {
    return this.http.post<any>(`${API_USER}/request-otp`, payload);
  }

  confirmUserOtp(payload: {user_id: string, otp: string}): Observable<any> {
    return this.http.post<any>(`${API_USER}/confirm-otp`, payload);
  }

  public changePassword(payload) {
    return this.http.put(`${API_UPDATE_PASSWORD}`, payload);
  }

  checkExists(data: CheckExistsModel): Observable<UserModel> {
    let endpoint;
    if (data.email) {
      endpoint = `${API_ENDPOINT}auth/check/email/${data.email}`;
    } else if (data.phone) {
      endpoint = `${API_ENDPOINT}auth/check/phone/${data.phone}`;
    } else if (data.username) {
      endpoint = `${API_ENDPOINT}auth/check/username/${data.username}`;
    }
    return this.http
      .get<UserModel>(endpoint)
      .pipe(catchError(this.handleError('check-exist', 0)));
  }

  public getAdvice(): Observable<any> {
    return this.http
      .get<any>(`${API_THIRD_URL}/get-advice`)
      .pipe(catchError(this.handleError('get-advice', 0)));
  }

  getAllUsers(): Observable<UserModel[]> {
    return this.http.get<UserModel[]>(API_USERS_URL);
  }

  getUserById(userId: string): Observable<UserModel> {
    return this.http.get<UserModel>(`${API_USERS_URL}/${userId}`);
  }

  getCustomerById(userId: string): Observable<UserModel> {
    return this.http.get<UserModel>(API_USERS_URL + '/user/' + userId);
  }

  getStaffById(userId: string): Observable<UserModel> {
    return this.http.get<UserModel>(API_USERS_URL + '/settings/' + userId);
  }

  getUserByPhone(phone: string): Observable<UserModel> {
    return this.http.get<UserModel>(
      API_USERS_URL + `/${phone}` + '/phone'
    );
  }

  getUserByEmail(email: string): Observable<UserModel> {
    return this.http.get<UserModel>(
      API_USERS_URL + `/${email}` + '/email'
    );
  }

  // UPDATE => PUT: update the settings on the server
  updateUser(_user: UserModel | MiniUserModel): Observable<any> {
    return this.http.put(`${API_USERS_URL}/${_user.publicId}/edit`, _user);
  }

  // DELETE => delete the settings from the server
  deleteUser(userId: string) {
    const url = `${API_USERS_URL}/${userId}`;
    return this.http.delete(url);
  }

  // CREATE =>  POST: add a new role to the server
  saveVisitor(visitor: VisitorModel): Observable<{success: boolean}> {
    return this.http.post<{success: boolean}>(API_USER + '/save-visitor', visitor);
  }

  sendFeedback(feedback: any): Observable<boolean> {
    return this.http.post<boolean>(API_USER + '/feedback', feedback);
  }

  createUser(user: UserModel): Observable<UserModel> {
    return this.http.post<UserModel>(API_USERS_URL, user);
  }

  insertCustomer(user: MiniUserModel): Observable<any> {
    return this.http.post<any>(API_USERS_URL + '/insert', user);
  }

  insertStaff(user: MiniUserModel): Observable<any> {
    return this.http.post<any>(API_USERS_URL + '/insert', user);
  }

  // Method from server should return QueryResultsModel(items: any[], totalsCount: number)
  // items => filtered/sorted result
  findUsers(queryParams: QueryParamsModel): Observable<QueryResultsModel> {
    return this.http.post<QueryResultsModel>(
      `${API_USERS_URL}/find`,
      queryParams
    );
  }

  /*
   * Handle Http operation that failed.
   * Let the app continue.
   *
   * @param operation - title of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(operation = 'operation', result?: any) {
    return (error: any): Observable<any> => {
      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // Let the app keep running by returning an empty result.
      return of(result);
    };
  }
}
